import { CreateLabelInput } from '@aibees/elephant/components/create-label-input';
import { LabelColorPicker } from '@aibees/elephant/components/label-color-picker';
import { useCreateList } from '@aibees/elephant/hooks/labels/use-create-list';
import { useEditList } from '@aibees/elephant/hooks/labels/use-edit-list';
import { useLabelApiType } from '@aibees/elephant/hooks/labels/use-label-api-type';
import { useLabelColor } from '@aibees/elephant/hooks/labels/use-label-color';
import { useLabelGroupingStatus } from '@aibees/elephant/hooks/labels/use-label-grouping-status';
import { useLabelVisibilityStatus } from '@aibees/elephant/hooks/labels/use-label-visibility-status';
import { LabelType } from '@aibees/elephant/types/label';
import { notificationIconMap } from '@aibees/elephant/utils';
import {
  FormOnSubmit,
  Grid2,
  VisibilitySwitch,
} from '@aibees/shared/components';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Button, Stack, Typography } from '@mui/material';
import { random } from 'lodash-es';
import { memo, useEffect } from 'react';
import { formConfig } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import type { CreateLabelFormProps as Props } from './types';
import { useLabelFormState } from './use-label-form-state';

export const CreateLabelForm = memo(
  ({
    className,
    isEdit,
    defaultValues,
    grouping,
    item,
    onSuccess,
    applyState,
  }: Props) => {
    const notify = useNotify();
    const groupingStatus = useLabelGroupingStatus();
    const addApi = useCreateList();
    const editApi = useEditList(item?.id || '');
    const api = useLabelApiType(addApi, editApi, isEdit);

    useEffect(() => {
      const isChanging = addApi.isLoading || editApi.isLoading;
      applyState?.setValue(isChanging);
    }, [addApi, editApi]);

    const {
      labelName,
      checked,
      colors,
      onChange,
      handleColorChange,
      handleVisibilityChange,
      isFormChanged,
    } = useLabelFormState({ isEdit, item });

    const visibility = useLabelVisibilityStatus(checked);
    const color = useLabelColor(colors);

    const handleSubmit: FormOnSubmit = (data) => {
      if (isEdit) {
        const editData = {
          id: defaultValues?.id,
          grouping: grouping ?? groupingStatus,
          name: labelName,
          description: data.description,
          type: LabelType.LABEL,
          color: color as string,
          visibility,
        };
        editApi.mutateAsync(editData).then(() => {
          notify.complete(
            'Labels Edited!',
            'Label updated successfully.',
            random(1, 101),
            {
              icon: notificationIconMap.Label,
            }
          );
          onSuccess?.();
        });
      } else {
        const addData = {
          grouping: grouping ?? groupingStatus,
          name: labelName,
          description: data.description,
          type: LabelType.LABEL,
          color: color as string,
          visibility,
        };
        addApi.mutateAsync(addData).then((payload) => {
          notify.complete(
            'Labels Created!',
            'Your New Labels Added.',
            random(1, 101),
            {
              icon: notificationIconMap.Label,
            }
          );
          onSuccess?.(payload);
        });
      }
    };

    return (
      <Styled.Wrapper
        className={classnames('CreateLabelForm', className)}
        sx={SX.root}
      >
        <Styled.Form
          {...formConfig}
          customSubmitButton
          loading={api.isLoading}
          defaultValues={item}
          actionsSx={SX.action}
          onSubmit={handleSubmit}
          resetAfterSubmit
          sx={SX.form}
        >
          <Grid2 xs={12} sx={SX.row}>
            <Typography sx={SX.label}>Create New Label</Typography>

            <VisibilitySwitch
              checked={checked}
              onChange={handleVisibilityChange}
            />
          </Grid2>
          <Grid2 container sx={SX.formElements} spacing={0.5}>
            <Grid2 xs={12} lg={9}>
              <CreateLabelInput
                name="name"
                placeholder="Enter Label name"
                value={labelName}
                onChange={onChange}
              />
            </Grid2>

            <Grid2 xs={12} lg={3}>
              <Button
                type="submit"
                children={isEdit ? 'Save' : 'Create Label'}
                variant="contained"
                color="primary"
                size="small"
                sx={SX.submitButton}
                disabled={
                  api.isLoading || !labelName || (isEdit && !isFormChanged)
                }
              />
            </Grid2>
          </Grid2>

          <Stack gap={0.5}>
            <Typography sx={SX.label}>Description(Optional)</Typography>

            <CreateLabelInput
              name="description"
              placeholder="Add a Description ..."
              multiline
              rows={2}
            />
          </Stack>

          <LabelColorPicker
            colors={colors}
            handleColorChange={handleColorChange}
          />
        </Styled.Form>
      </Styled.Wrapper>
    );
  }
);

CreateLabelForm.displayName = 'CreateLabelForm';
